import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { 
  doc, getDoc, collection, query, where, 
  onSnapshot, orderBy, updateDoc, limit, writeBatch 
} from "firebase/firestore";
import {
  HiOutlineHome, HiOutlineCurrencyDollar, HiOutlineUser,
  HiOutlineLogout, HiOutlineBell, HiOutlineSearch,
  HiOutlineShieldCheck, HiMenu, HiX,
} from "react-icons/hi";
import "./UserDashboard.css";
import Overview from "./Overview";
import Profile from "./Profile";
import Revenue from "./Revenue";

const UserDashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("home");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  useEffect(() => {
    let unsubscribeNotifs = () => {};

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) setUserData(docSnap.data());

          const notifQuery = query(
            collection(db, "notifications"),
            where("recipientId", "==", user.uid),
            where("isRead", "==", false), 
            orderBy("createdAt", "desc"),
            limit(15)
          );

          unsubscribeNotifs = onSnapshot(notifQuery, (snapshot) => {
            const notifs = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setNotifications(notifs);
          }, (err) => console.error("Snapshot error:", err));

        } catch (error) {
          console.error("Initialization error:", error);
        } finally {
          // Keep loading for a tiny bit longer for a smooth transition
          setTimeout(() => setLoading(false), 800);
        }
      } else {
window.location.href = "/login";
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeNotifs();
    };
  }, [navigate]);

  const handleMarkAsRead = async (notifId) => {
    try {
      const notifRef = doc(db, "notifications", notifId);
      await updateDoc(notifRef, { isRead: true });
      window.location.reload(); 
    } catch (error) {
      console.error("Error marking read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (notifications.length === 0) return;
    try {
      const batch = writeBatch(db);
      notifications.forEach((n) => {
        const ref = doc(db, "notifications", n.id);
        batch.update(ref, { isRead: true });
      });
      await batch.commit();
      window.location.reload();
    } catch (error) {
      console.error("Error clearing all:", error);
    }
  };

  const unreadCount = notifications.length;

  const renderContent = () => {
    switch (activeTab) {
      case "home": return <Overview userData={userData} />;
      case "earnings": return <Revenue />;
      case "profile": return <Profile userData={userData} />;
      default: return <Overview userData={userData} />;
    }
  };

  // --- NEW CIRCLE LOADING ICON ---
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className={`app-container ${sidebarOpen ? "sidebar-visible" : ""}`}>
      {sidebarOpen && <div className="sidebar-overlay visible" onClick={() => setSidebarOpen(false)}></div>}

      <aside className={`glass-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="brand-header">
          <div className="brand-logo"><div className="logo-inner"></div></div>
          <div className="brand-name">User <span>Portal</span></div>
          <button className="mobile-close-btn" onClick={() => setSidebarOpen(false)}><HiX /></button>
        </div>

        <nav className="side-nav">
          <p className="nav-label">Main Menu</p>
          <button className={activeTab === "home" ? "nav-link active" : "nav-link"} onClick={() => { setActiveTab("home"); setSidebarOpen(false); }}>
            <HiOutlineHome /> Overview
          </button>
          <button className={activeTab === "earnings" ? "nav-link active" : "nav-link"} onClick={() => { setActiveTab("earnings"); setSidebarOpen(false); }}>
            <HiOutlineCurrencyDollar /> Revenue
          </button>
          <p className="nav-label" style={{ marginTop: "20px" }}>Account</p>
          <button className={activeTab === "profile" ? "nav-link active" : "nav-link"} onClick={() => { setActiveTab("profile"); setSidebarOpen(false); }}>
            <HiOutlineUser /> Profile
          </button>
        </nav>

        <div className="pro-card">
          <div className="pro-glow"></div>
          <div className="pro-card-header"><HiOutlineShieldCheck className="pro-icon" /><h4>Enterprise</h4></div>
          <p>Upgrade for more features.</p>
          <button className="pro-btn" onClick={() => navigate("/plans")}>Upgrade Now</button>
        </div>

        <button onClick={() => signOut(auth)} className="logout-action"><HiOutlineLogout /> Sign Out</button>
      </aside>

      <main className="main-interface">
        <header className="interface-header">
          <div className="header-left-section">
            <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}><HiMenu /></button>
            <div className="header-search">
              <HiOutlineSearch />
              <input type="text" placeholder="Search anything ..." />
            </div>
          </div>

          <div className="header-utils">
            <div className="notification-container">
              <div 
                className={`notification-bell ${unreadCount > 0 ? "has-unread" : ""}`} 
                onClick={() => setShowNotifPanel(!showNotifPanel)}
              >
                <HiOutlineBell />
                {unreadCount > 0 && <span className="bell-badge-red">{unreadCount}</span>}
              </div>

              {showNotifPanel && (
                <div className="notif-dropdown-panel">
                  <div className="notif-panel-header">
                    <span>New ({unreadCount})</span>
                    {unreadCount > 0 && (
                      <button className="mark-all-btn" onClick={handleMarkAllAsRead}>
                        Mark all as read
                      </button>
                    )}
                  </div>
                  <div className="notif-panel-body">
                    {notifications.length === 0 ? (
                      <div className="notif-empty">All caught up! 🎉</div>
                    ) : (
                      notifications.map((notif) => (
                        <div 
                          key={notif.id} 
                          className="notif-card is-unread"
                          onClick={() => handleMarkAsRead(notif.id)}
                        >
                          <div className="notif-icon-circle">💰</div>
                          <div className="notif-content">
                            <p className="notif-title">{notif.title || "Update"}</p>
                            <p className="notif-msg">{notif.message}</p>
                            <span className="notif-time">
                                {notif.createdAt ? notif.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Just now"}
                            </span>
                          </div>
                          <div className="unread-indicator-dot"></div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="user-profile">
              <div className="user-info">
                <span className="user-name">{userData?.email}</span>
                <span className="user-plan">{userData?.hosting || "Standard"}</span>
              </div>
              <div className="avatar-circle">{userData?.email?.charAt(0).toUpperCase() || "U"}</div>
            </div>
          </div>
        </header>

        <div className="content-area">{renderContent()}</div>
      </main>
    </div>
  );
};

export default UserDashboard;